/**
 * Asset Type Mapping
 * Maps layer types to SVG asset file paths
 */

export const ASSET_TYPE_TO_PATH: Record<string, string> = {
  // CCTV (조명 CCTV 가 일반 cctv 레이어의 기본 아이콘)
  'c-cctv': '/assets/parking/lightCctv.svg',
  'c-cctv-ip': '/assets/parking/lightCctv.svg',
  'c-cctv-id': '/assets/parking/lightCctv.svg',
  'p-cctv': '/assets/parking/lightCctv.svg',
  // 매뉴얼 placement 용 canonical layer key
  'p-lightCctv': '/assets/parking/lightCctv.svg',
  'p-warningCctv': '/assets/parking/warningCctv.svg',
  // DXF 표준 kebab-case (LayerMappingModal 에서 사용)
  'p-light-cctv': '/assets/parking/lightCctv.svg',
  'p-warning-cctv': '/assets/parking/warningCctv.svg',

  // Emergency
  'c-emergencybell': '/assets/parking/bell.svg',
  'p-emergency': '/assets/parking/bell.svg',

  // Chargers & Electric (전기 충전기)
  'e-charger': '/assets/parking/charger.svg',
  'c-carCharger': '/assets/parking/charger.svg',
  'p-parking-large-electric': '/assets/electric.svg',
  'p-parking-electric': '/assets/electric.svg',

  // Parking Types
  'p-parking-basic': '/assets/common.svg',
  'p-parking-large': '/assets/common.svg',
  'p-parking-large-women': '/assets/common.svg',
  'p-parking-small': '/assets/small_car.svg',
  'p-parking-disable': '/assets/handicap.svg',
  'p-parking-disabled': '/assets/handicap.svg',

  // Facilities
  'e-elevator': '/assets/parking/elevator.svg',
  'e-entrance': '/assets/marker.svg',
  'e-onepassreader': '/assets/marker.svg',

  // Lighting
  'l-lightingline': '/assets/light.svg',
  'l-preventionlight': '/assets/preventionLights.svg',
  'l-light': '/assets/light.svg',  // Generic light from DXF parser

  // Default fallback
  'default': '/assets/common.svg'
}

/**
 * Get asset path for a given layer type
 */
export function getAssetPath(layer: string): string {
  // Direct match
  if (ASSET_TYPE_TO_PATH[layer]) {
    return ASSET_TYPE_TO_PATH[layer]
  }

  // Partial match (prefix)
  for (const [key, path] of Object.entries(ASSET_TYPE_TO_PATH)) {
    if (layer.startsWith(key)) {
      return path
    }
  }

  // Default fallback
  return ASSET_TYPE_TO_PATH['default']
}

/**
 * Get fill color based on layer type
 */
/**
 * Get fill color based on layer type
 */
export function getLayerFillColor(layer: string): string {
  // Stroke-only layers (no fill)
  if (layer.startsWith('l-lightinglineframe')) {
    return 'none'
  }
  // s-outline: 외곽선은 시각 표시용 polyline — fill 없음 stroke 만
  if (layer.startsWith('s-outline')) {
    return 'none'
  }

  if (layer.startsWith('p-parking-large-electric')) {
    return 'var(--color-map-parking-electric-fill)'
  }
  if (layer.startsWith('p-parking-large-women')) {
    return 'var(--color-map-parking-women-fill)'
  }
  if (layer.startsWith('p-parking-disable')) {
    return 'var(--color-map-parking-disable-fill)'
  }
  if (layer.startsWith('p-parking-small')) {
    return 'var(--color-map-parking-basic-fill)' // Using basic for small for now or add specific
  }
  if (layer.startsWith('p-parking-large')) {
    return 'var(--color-map-parking-basic-fill)' // Using basic for large for now or add specific
  }
  if (layer.startsWith('p-parking-basic')) {
    return 'var(--color-map-parking-basic-fill)'
  }
  if (layer.startsWith('e-elevator')) {
    return 'rgba(198, 176, 188, 0.3)' // Semi-transparent purple/pink
  }
  if (layer.startsWith('e-zone-area')) {
    return 'var(--color-map-line)' // Using line color for zones with low opacity
  }
  if (layer.startsWith('p-guideboard-area')) {
    return 'var(--color-map-line)'
  }

  // Default
  return 'var(--color-map-line)'
}

/**
 * Get stroke color based on layer type
 */
export function getLayerStrokeColor(layer: string): string {
  if (layer.startsWith('p-parking-large-electric')) {
    return 'var(--color-map-parking-electric-stroke)'
  }
  if (layer.startsWith('p-parking-large-women')) {
    return 'var(--color-map-parking-women-stroke)'
  }
  if (layer.startsWith('p-parking-disable')) {
    return 'var(--color-map-parking-disable-stroke)'
  }
  if (layer.startsWith('p-parking-small')) {
    return 'var(--color-map-parking-basic-stroke)'
  }
  if (layer.startsWith('p-parking-large')) {
    return 'var(--color-map-parking-basic-stroke)'
  }
  if (layer.startsWith('e-elevator')) {
    return '#c6b0bc' // Purple/pink border
  }
  if (layer.startsWith('e-outline') || layer.startsWith('s-outline')) {
    return 'var(--color-map-line)'
  }
  if (layer.startsWith('e-innerline')) {
    return 'var(--color-map-line)'
  }
  if (layer.startsWith('l-lightingline')) {
    return 'var(--color-map-line)'
  }

  // Default
  return 'var(--color-map-line)'
}

/**
 * Get icon size based on layer type
 */
export function getIconSize(layer: string): { width: number; height: number } {
  if (
    layer.startsWith('c-cctv') ||
    layer.startsWith('p-cctv') ||
    layer === 'p-lightCctv' ||
    layer === 'p-warningCctv' ||
    layer === 'p-light-cctv' ||
    layer === 'p-warning-cctv'
  ) {
    return { width: 2, height: 2 }
  }
  if (layer.startsWith('e-charger') || layer === 'c-carCharger') {
    // 전기 충전기 기본 크기 (사용자 요청): 9.25m × 9.25m
    return { width: 9.25, height: 9.25 }
  }
  if (layer.startsWith('c-emergencybell') || layer.startsWith('p-emergency')) {
    return { width: 2, height: 2 }
  }
  if (layer.startsWith('e-elevator')) {
    return { width: 35, height: 35 }
  }
  if (layer.startsWith('l-lightingline')) {
    return { width: 1.5, height: 1.5 }
  }
  if (layer.startsWith('e-entrance')) {
    return { width: 25, height: 25 }
  }

  // Default
  return { width: 20, height: 20 }
}

/**
 * Check if layer should be rendered as point (icon)
 */
export function isPointLayer(layer: string): boolean {
  const pointPrefixes = [
    'c-cctv',
    'p-cctv',
    'p-lightCctv',
    'p-warningCctv',
    'p-light-cctv',
    'p-warning-cctv',
    'c-emergencybell',
    'p-emergency',
    'e-charger',
    'c-carCharger',
    'e-pillar',
    'l-preventionlight',
    'e-entrance',
    'e-onepassreader'
  ]

  return pointPrefixes.some(prefix => layer.startsWith(prefix))
}

/**
 * Check if layer should be rendered as polygon
 */
export function isPolygonLayer(layer: string): boolean {
  const polygonPrefixes = [
    'p-parking-',
    'e-zone-area',
    'p-guideboard-area',
    'e-elevator',
    'l-lightinglineframe'
  ]

  return polygonPrefixes.some(prefix => layer.startsWith(prefix))
}

/**
 * Check if layer should be rendered as line
 */
export function isLineLayer(layer: string): boolean {
  const linePrefixes = [
    'e-outline',
    's-outline',
    'e-innerline',
    'l-lightingline',
    'l-light',
    'e-drivewayline'
  ]

  return linePrefixes.some(prefix => layer.startsWith(prefix))
}

/**
 * Check if layer is text/label
 */
export function isTextLayer(layer: string): boolean {
  const textPrefixes = [
    'p-parking-cctvid',
    'c-cctv-ip',
    'c-cctv-id',
    'e-zone-nametext'
  ]

  return textPrefixes.some(prefix => layer.startsWith(prefix))
}
